import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dropdown } from '@/components/ui/dropdown';
import axios from '../utils/axiosInstance';
import { Button } from "@/components/ui/button";
import { PageLoader } from '@/components/ui/page-loader';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Trash2, Trophy } from 'lucide-react';
import { BASE_URL } from '../config/constants';
import { toast } from 'sonner';
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { DragEndEvent } from '@dnd-kit/core';
import { usePlan } from '@/context/PlanContext';
import { PlanLimitBadge, GracePeriodBanner, PlanItemBadge } from '@/components/PlanComponents';
import SectionTextSettingsCard from '@/components/SectionTextSettingsCard';
import { Textarea } from '@/components/ui/textarea';


interface AwardAsset {
  _id: string;
  name: string;
  image: string;
  category: string;
  description: string;
  isActive: boolean;
}

interface Award {
  _id: string;
  title: string;
  year: number;
  description: string;
  iconImageAssetId?: string;
  iconImage?: string;
  assetCategory?: string;
  awardsList: any[];
  isActive: boolean;
  order: number;
}

interface AwardsSectionManagerProps {
  domain: string;
}

const emptyAward: Omit<Award, '_id'> = {
  title: '',
  year: new Date().getFullYear(),
  description: '',
  iconImageAssetId: '',
  iconImage: '',
  assetCategory: '',
  awardsList: [],
  isActive: true,
  order: 0,
};

const SortableAwardRow: React.FC<{ item: Award; index: number; onEdit: (item: Award) => void; onDelete: (id: string) => void; onToggle: (id: string, currentStatus: boolean) => void; togglingId: string | null }> = ({ item, index, onEdit, onDelete, onToggle, togglingId }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item._id });
  const style = { transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined, transition, backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined };

  return (
    <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
      <TableCell className="w-10 select-none cursor-grab">
        <div className="flex items-center gap-2" {...attributes} {...listeners}>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted-foreground">
            <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
            <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
          </svg>
          <span className="w-4 text-muted-foreground font-medium">{index + 1}</span>
        </div>
      </TableCell>
      <TableCell className="w-16">
        {(item.iconImage || item.iconImageAssetId) && (
          <img src={item.iconImage} alt={item.title} className="w-16 h-16 object-cover rounded" />
        )}
      </TableCell>
      <TableCell className="font-medium text-foreground max-w-[200px]">
        <div className="break-words whitespace-normal">
          {item.title}
        </div>
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{item.year}</TableCell>
      <TableCell className="text-sm text-muted-foreground max-w-[250px]">
        <div className="break-words whitespace-normal">
          {item.description?.substring(0, 100)}...
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Switch
            checked={item.isActive}
            onCheckedChange={() => onToggle(item._id, item.isActive)}
            aria-label="Toggle award active state"
            disabled={togglingId === item._id}
          />
          <span className={item.isActive ? 'status-label-active' : 'status-label-inactive'}>{item.isActive ? 'Active' : 'Inactive'}</span>
        </div>
      </TableCell>
      <TableCell>
        <PlanItemBadge resourceType="awards" index={index} />
      </TableCell>
      <TableCell>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="icon-sm" 
            onClick={() => onEdit(item)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </Button>
          <Button 
            variant="destructive" 
            size="icon-sm" 
            onClick={() => onDelete(item._id)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
};

const AwardsSectionManager: React.FC<AwardsSectionManagerProps> = ({ domain }) => {
  const { canAdd, openUpgradeModal, adjustUsage } = usePlan();
  const location = useLocation();
  const navigate = useNavigate();
  const [awards, setAwards] = useState<Award[]>([]);
  const [assets, setAssets] = useState<AwardAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyAward);
  const [editId, setEditId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [_sectionActive, setSectionActive] = useState(true);
  const [_sectionConfigLoading, setSectionConfigLoading] = useState(true);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchAwards = () => {
    setLoading(true);
    axios.get(BASE_URL + '/api/awards-section')
      .then(res => {
        // Ensure res.data is always an array - handle wrapped data
        let data = res.data;
        if (Array.isArray(data)) {
          setAwards(data);
        } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
          setAwards(data.data);
        } else {
          setAwards([]);
        }
      })
      .catch(err => {
        console.error('Error fetching awards:', err);
        setAwards([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchAssets = () => {
    axios.get(BASE_URL + '/api/award-assets')
      .then(res => setAssets(res.data))
      .catch(err => {
        console.error('Error fetching assets:', err);
        setAssets([]);
      });
  };

  const fetchSectionConfig = () => {
    setSectionConfigLoading(true);
    axios.get(BASE_URL + '/api/awards-section/config/get')
      .then(res => setSectionActive(res.data.sectionActive))
      .catch(err => {
        console.error('Error fetching section config:', err);
        setSectionActive(true);
      })
      .finally(() => setSectionConfigLoading(false));
  };

  useEffect(() => {
    fetchAssets();
    fetchSectionConfig();
  }, []);

  useEffect(() => {
    if (domain) {
      fetchAwards();
    }
  }, [domain]);

  // Auto-open the "Add Award" dialog when navigated here from the dashboard
  // card (state.openAdd). Mirrors the Add button's plan-limit behaviour, then
  // clears the navigation state so it doesn't re-trigger on refresh/back.
  useEffect(() => {
    if (!(location.state as { openAdd?: boolean } | null)?.openAdd) return;
    navigate(location.pathname, { replace: true, state: null });
    if (!canAdd('awards')) {
      openUpgradeModal('awards');
    } else {
      setAddOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const yearOptions = Array.from({ length: 50 }, (_, i) => {
    const y = new Date().getFullYear() - i;
    return { value: y, label: String(y) };
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : (name === 'year' ? parseInt(value) : value);
    setForm(prev => ({ ...prev, [name]: newValue }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const response = await axios.post(BASE_URL + '/api/awards-section', form);
      setAddOpen(false);
      setForm(emptyAward);
      toast.success('Award added successfully!');
      adjustUsage('awards', 1);

      const savedData = response.data?.data || response.data;
      if (savedData?._id) {
        setAwards(prev => [...prev, savedData]);
      } else {
        fetchAwards();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to add award';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: Award) => {
    setEditId(item._id);
    setForm(item);
    setEditOpen(true);
    setError('');
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const currentEditId = editId;
    try {
      const response = await axios.put(`${BASE_URL}/api/awards-section/${editId}`, form);
      setEditOpen(false);
      setEditId(null);
      setForm(emptyAward);
      toast.success('Award updated successfully!');
 
      const savedData = response.data?.data || response.data;
      if (savedData?._id) {
        setAwards(prev => prev.map(award =>
          award._id === currentEditId ? savedData : award
        ));
      } else {
        fetchAwards();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update award';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    const deletedAward = awards.find(award => award._id === deleteId);
    // Optimistic removal
    setAwards(prev => prev.filter(award => award._id !== deleteId));
    setDeleteId(null);
    try {
      await axios.delete(`${BASE_URL}/api/awards-section/${deleteId}`);
      toast.success('Award deleted successfully!');
      adjustUsage('awards', -1);

    } catch (err: any) {
      // Revert on error
      if (deletedAward) {
        setAwards(prev => [...prev, deletedAward].sort((a, b) => a.order - b.order));
      }
      toast.error(err.response?.data?.message || 'Failed to delete award');
    }
  };

  const handleToggle = async (id: string, currentStatus: boolean) => {
    setTogglingId(id);
    // Optimistic update
    setAwards(prev => prev.map(award =>
      award._id === id ? { ...award, isActive: !currentStatus } : award
    ));
    try {
      await axios.patch(`${BASE_URL}/api/awards-section/${id}/toggle-status`, { isActive: !currentStatus });
      toast.success(`Award ${currentStatus ? 'deactivated' : 'activated'} successfully!`);
    } catch (err: any) {
      // Revert on error
      setAwards(prev => prev.map(award =>
        award._id === id ? { ...award, isActive: currentStatus } : award
      ));
      const errorMessage = err.response?.data?.message || 'Failed to toggle award status';
      toast.error(errorMessage);
    } finally {
      setTogglingId(null);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = awards.findIndex((item) => item._id === active.id);
      const newIndex = awards.findIndex((item) => item._id === over.id);
      const newItems = arrayMove(awards, oldIndex, newIndex);
      setAwards(newItems);
      try {
        await axios.post(`${BASE_URL}/api/awards-section/reorder`, { items: newItems });
        toast.success('Awards reordered successfully!');
      } catch (err: any) {
        toast.error(err.response?.data?.message || 'Failed to reorder awards');
        fetchAwards();
      }
    }
  };

  return (
    <div>
      <GracePeriodBanner />
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="page-title">Awards Section</h2>
          <PlanLimitBadge resourceType="awards" />
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              onClick={(e) => {
                if (!canAdd('awards')) {
                  e.preventDefault();
                  e.stopPropagation();
                  openUpgradeModal('awards');
                }
              }}
            >
              Add Award
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Award</DialogTitle>
              <DialogDescription>Create a new award entry with title, year, description and icon from the asset library</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-5">
              <Input name="title" placeholder="Award Title" value={form.title} onChange={handleFormChange} required />
              <Dropdown
                options={yearOptions}
                value={form.year}
                onValueChange={(val) => setForm((prev: any) => ({ ...prev, year: val as number }))}
                placeholder="Select Year"
              />
              <Textarea name="description" placeholder="Description" value={form.description} onChange={handleFormChange} rows={3} />
              
              <div className="form-group">
                <Label className="form-label">Award Icon/Image</Label>
                <div className="flex gap-2">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setAssetSelectorOpen(true)}
                    className="flex-1"
                  >
                    {form.iconImageAssetId ? 'Change Award' : 'Select Award'}
                  </Button>
                  {form.iconImage && (
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      className="text-muted-foreground hover:text-danger"
                      onClick={() => setForm(prev => ({ ...prev, iconImage: '', iconImageAssetId: '', assetCategory: '' }))}
                    >
                      <Trash2 className="size-3.5" />
                      Remove
                    </Button>
                  )}
                </div>
                {form.iconImage && (
                  <div className="flex items-center gap-2">
                    <img src={form.iconImage} alt="selected" className="w-12 h-12 object-cover rounded" />
                    <span className="text-sm text-muted-foreground">Asset selected</span>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <Switch
                  checked={form.isActive}
                  onCheckedChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
                  aria-label="Toggle active state"
                />
                <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                  {form.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              {error && <div className="text-danger">{error}</div>}
              <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <SectionTextSettingsCard sectionKey="awards" />

      <div>
        {loading ? (
          <PageLoader label="Loading awards..." />
        ) : !Array.isArray(awards) || awards.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Trophy className="w-full h-full" />
            </div>
            <h3 className="empty-state-title">No awards found</h3>
            <p className="empty-state-description">Add your first award to showcase your achievements</p>
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-10">Order</TableHead>
                  <TableHead className="w-16">Image</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <SortableContext items={awards.map(item => item._id)} strategy={verticalListSortingStrategy}>
                  {awards.map((item, idx) => (
                    <SortableAwardRow key={item._id} item={item} index={idx} onEdit={handleEdit} onDelete={setDeleteId} onToggle={handleToggle} togglingId={togglingId} />
                  ))}
                </SortableContext>
              </TableBody>
            </Table>
          </DndContext>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Award</DialogTitle>
            <DialogDescription>Update the award details including title, year, description and icon image</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-5">
            <Input name="title" placeholder="Award Title" value={form.title} onChange={handleFormChange} required />
            <Dropdown
              options={yearOptions}
              value={form.year}
              onValueChange={(val) => setForm((prev: any) => ({ ...prev, year: val as number }))}
              placeholder="Select Year"
            />
            <Textarea name="description" placeholder="Description" value={form.description} onChange={handleFormChange} rows={3} />
            
            <div className="form-group">
              <Label className="form-label">Award Icon/Image</Label>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setAssetSelectorOpen(true)}
                  className="flex-1"
                >
                  {form.iconImageAssetId ? 'Change Asset' : 'Select Asset'}
                </Button>
                {form.iconImage && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    className="text-muted-foreground hover:text-danger"
                    onClick={() => setForm(prev => ({ ...prev, iconImage: '', iconImageAssetId: '', assetCategory: '' }))}
                  >
                    <Trash2 className="size-3.5" />
                    Remove
                  </Button>
                )}
              </div>
              {form.iconImage && (
                <div className="flex items-center gap-2">
                  <img src={form.iconImage} alt="selected" className="w-12 h-12 object-cover rounded" />
                  <span className="text-sm text-muted-foreground">Asset selected</span>
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <Switch
                checked={form.isActive}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, isActive: checked }))}
                aria-label="Toggle active state"
              />
              <span className={form.isActive ? 'status-label-active' : 'status-label-inactive'}>
                {form.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            {error && <div className="text-danger">{error}</div>}
            <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        title="Delete Award"
        description="Are you sure you want to delete this award? This action cannot be undone."
        confirmLabel="Delete"
        onConfirm={handleDelete}
      />

      <Dialog open={assetSelectorOpen} onOpenChange={setAssetSelectorOpen}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Select Award Asset</DialogTitle>
            <DialogDescription>Choose a high-quality asset from the library for this award</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              type="text"
              placeholder="Search assets by name or category..."
              value={assetSearchQuery}
              onChange={(e) => setAssetSearchQuery(e.target.value)}
            />
            <div className="grid grid-cols-2 gap-4 max-h-96 overflow-y-auto">
              {assets.length === 0 ? (
                <div className="col-span-2 text-center py-8 text-muted-foreground">
                  No assets available. Please contact super-admin to create award assets.
                </div>
              ) : (
                assets
                  .filter(asset =>
                    asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
                    asset.category.toLowerCase().includes(assetSearchQuery.toLowerCase())
                  )
                  .map(asset => (
                    <div
                      key={asset._id}
                      onClick={() => {
                        setForm(prev => ({ 
                          ...prev, 
                          iconImageAssetId: asset._id, 
                          iconImage: asset.image,
                          assetCategory: asset.category
                        }));
                        setAssetSelectorOpen(false);
                        setAssetSearchQuery('');
                      }}
                      className="p-3 border rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                    >
                      <img src={asset.image} alt={asset.name} className="w-full h-32 object-cover rounded mb-2" />
                      <div className="text-sm font-medium">{asset.name}</div>
                      <div className="text-xs text-muted-foreground">{asset.category}</div>
                    </div>
                  ))
              )}
            </div>
            {assets.length > 0 && assets.filter(asset =>
              asset.name.toLowerCase().includes(assetSearchQuery.toLowerCase()) ||
              asset.category.toLowerCase().includes(assetSearchQuery.toLowerCase())
            ).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No assets found matching "{assetSearchQuery}"
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AwardsSectionManager;
